import { useState } from "react"
import { useWatch, UseFormReturn } from "react-hook-form-mui"
import { useUsersQuery } from "@/modules/telegram/store/telegramAdvApi"
import { UserFilters, UserFiltersForm } from "./userFilters"
import { useAppDispatch } from "@/modules/app/hooks"
import { setUsers } from "@/modules/telegram/store"
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material"
import { formatUsersQueryError, hasActiveFilters } from "./recipientGuards"

interface IProps {
  /** Filter form lives in the parent (react-hook-form) — not in redux — so it
   * is not persisted across page reloads. */
  filtersFormContext: UseFormReturn<UserFiltersForm>
}

export const MailingCredentialsFilters = ({ filtersFormContext }: IProps) => {
  const dispatch = useAppDispatch()

  // Explicit opt-in required to target the entire user base.
  const [allUsersOptIn, setAllUsersOptIn] = useState(false)

  const filterValues = useWatch({
    control: filtersFormContext.control,
  }) as UserFiltersForm
  const filtersActive = hasActiveFilters(
    filterValues as Record<string, unknown>,
  )
  // "Все пользователи" is mutually exclusive with filters: when ticked we
  // ignore any selected filters and request the whole base ({}). Otherwise we
  // require at least one active filter and never fire a default unfiltered request.
  const queryParams = allUsersOptIn ? {} : { ...filterValues }
  const skip = !allUsersOptIn && !filtersActive

  const { isFetching, currentData, isError, error, refetch } = useUsersQuery(
    { queryParams },
    { skip },
  )

  const count = currentData?.length ?? 0
  const canApply = !skip && !isFetching && !isError && count > 0

  const applyUsers = () => {
    if (!canApply) return
    dispatch(
      setUsers(currentData?.map((m) => `${m.token}/${m.telegram_id}`) ?? []),
    )
  }

  return (
    <Stack spacing={1}>
      {/* Filters are disabled while "Все пользователи" is active. */}
      <UserFilters formContext={filtersFormContext} disabled={allUsersOptIn} />

      <FormControlLabel
        sx={{ alignSelf: "flex-start", mr: 0 }}
        control={
          <Checkbox
            checked={allUsersOptIn}
            onChange={(e) => setAllUsersOptIn(e.target.checked)}
          />
        }
        label="Все пользователи (без фильтров)"
      />

      {skip && (
        <Typography variant="body2" color="text.secondary">
          Выберите фильтры или включите «Все пользователи», чтобы загрузить
          получателей.
        </Typography>
      )}

      {!skip && isFetching && <CircularProgress />}

      {!skip && !isFetching && isError && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => refetch()}>
              Повторить
            </Button>
          }
        >
          {formatUsersQueryError(error)}
        </Alert>
      )}

      {!skip && !isFetching && !isError && (
        <Box>
          <Typography>Найдено {count} пользователей</Typography>
          <Button onClick={applyUsers} disabled={!canApply}>
            Применить
          </Button>
        </Box>
      )}
    </Stack>
  )
}
